export const umidPrefix = "umid_";
export const umidBodyLength = 26;
export const umidPattern = /^umid_[0-9abcdefghjkmnpqrstvwxyz]{26}$/;

export const globalPersonStatuses = ["active", "disabled", "deleted_pending_retention"] as const;

export type GlobalPersonStatus = (typeof globalPersonStatuses)[number];

export type GlobalPersonEmail = {
  default: string | null;
  normalized: string | null;
  verified: boolean;
};

export type GlobalPersonProfileCompleteness = {
  hasDisplayName: boolean;
  hasEmail: boolean;
  hasPhoneNumber: boolean;
  hasPhoto: boolean;
};

export type GlobalPersonSource = {
  authProviderId: string | null;
  createdBy: "system:ensureCurrentPerson" | "seed:development" | "migration:hvicc";
};

export type GlobalPersonMigrationMetadata = {
  adminFlagMapped: boolean;
  legacyFirebaseUid: string | null;
  legacySource: "hvicc_flutterflow_users" | null;
};

export type GlobalPerson = {
  uid: string;
  firebaseUid: string;
  umid: string;
  displayName: string;
  email: GlobalPersonEmail;
  photoUrl: string | null;
  phoneNumber: string | null;
  status: GlobalPersonStatus;
  profileCompleteness: GlobalPersonProfileCompleteness;
  source: GlobalPersonSource;
  migration: GlobalPersonMigrationMetadata;
  createdAt: string | null;
  updatedAt: string | null;
};

export const selfEditableGlobalPersonFields = ["displayName", "photoUrl", "phoneNumber"] as const;

export const serverControlledGlobalPersonFields = [
  "uid",
  "firebaseUid",
  "umid",
  "email",
  "status",
  "profileCompleteness",
  "source",
  "migration",
  "createdAt",
  "updatedAt",
] as const;

export const forbiddenGlobalPersonAuthorizationFields = [
  "admin",
  "role",
  "roles",
  "permissions",
  "platformRole",
  "platformRoles",
  "organizationId",
  "organizationIds",
  "organizationRole",
  "organizationRoles",
] as const;

export type PersonIdentityErrorCode =
  "authentication_required" | "network_failure" | "invalid_response" | "forbidden" | "unknown";

export class PersonIdentityError extends Error {
  constructor(
    public readonly code: PersonIdentityErrorCode,
    message = personIdentityErrorMessage(code),
  ) {
    super(message);
    this.name = "PersonIdentityError";
  }
}

export type CurrentPersonFetch = (
  input: string,
  init: {
    headers: Record<string, string>;
    method: "POST";
  },
) => Promise<{
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}>;

export type EnsureCurrentPersonViaHttpInput = {
  apiBaseUrl: string;
  fetchClient: CurrentPersonFetch;
  getIdToken: () => Promise<string | undefined>;
};

export function isValidUmid(value: string): boolean {
  return umidPattern.test(value);
}

export function normalizePersonEmail(value: string | null | undefined): string | null {
  const normalized = value?.trim().toLowerCase() ?? "";
  return normalized.length > 0 ? normalized : null;
}

export function buildGlobalPersonEmail(input: {
  email: string | null | undefined;
  verified: boolean;
}): GlobalPersonEmail {
  const normalized = normalizePersonEmail(input.email);
  return {
    default: normalized,
    normalized,
    verified: input.verified,
  };
}

export function buildGlobalPersonProfileCompleteness(input: {
  displayName: string;
  email: GlobalPersonEmail;
  phoneNumber: string | null;
  photoUrl: string | null;
}): GlobalPersonProfileCompleteness {
  return {
    hasDisplayName: input.displayName.trim().length > 0,
    hasEmail: input.email.normalized !== null,
    hasPhoneNumber: (input.phoneNumber ?? "").trim().length > 0,
    hasPhoto: (input.photoUrl ?? "").trim().length > 0,
  };
}

export function containsForbiddenGlobalPersonAuthorizationField(
  data: Record<string, unknown>,
): boolean {
  return forbiddenGlobalPersonAuthorizationFields.some((field) => Object.hasOwn(data, field));
}

export function globalPersonFromApiResponse(value: unknown): GlobalPerson {
  const record = requireRecord(value, "person");
  const email = requireRecord(record.email, "person.email");
  const profileCompleteness = requireRecord(
    record.profileCompleteness,
    "person.profileCompleteness",
  );
  const source = requireRecord(record.source, "person.source");
  const migration = requireRecord(record.migration, "person.migration");

  const person: GlobalPerson = {
    uid: readRequiredString(record, "uid"),
    firebaseUid: readRequiredString(record, "firebaseUid"),
    umid: readRequiredString(record, "umid"),
    displayName: readRequiredString(record, "displayName"),
    email: {
      default: readNullableString(email, "default"),
      normalized: readNullableString(email, "normalized"),
      verified: readRequiredBoolean(email, "verified"),
    },
    photoUrl: readNullableString(record, "photoUrl"),
    phoneNumber: readNullableString(record, "phoneNumber"),
    status: readGlobalPersonStatus(record.status),
    profileCompleteness: {
      hasDisplayName: readRequiredBoolean(profileCompleteness, "hasDisplayName"),
      hasEmail: readRequiredBoolean(profileCompleteness, "hasEmail"),
      hasPhoneNumber: readRequiredBoolean(profileCompleteness, "hasPhoneNumber"),
      hasPhoto: readRequiredBoolean(profileCompleteness, "hasPhoto"),
    },
    source: {
      authProviderId: readNullableString(source, "authProviderId"),
      createdBy: readGlobalPersonSource(source.createdBy),
    },
    migration: {
      adminFlagMapped: readRequiredBoolean(migration, "adminFlagMapped"),
      legacyFirebaseUid: readNullableString(migration, "legacyFirebaseUid"),
      legacySource: readNullableLegacySource(migration, "legacySource"),
    },
    createdAt: readNullableString(record, "createdAt"),
    updatedAt: readNullableString(record, "updatedAt"),
  };

  if (person.uid !== person.firebaseUid) {
    throw new PersonIdentityError("invalid_response", "Person UID binding is invalid.");
  }

  if (!isValidUmid(person.umid)) {
    throw new PersonIdentityError("invalid_response", "Person UMID is invalid.");
  }

  if (containsForbiddenGlobalPersonAuthorizationField(record)) {
    throw new PersonIdentityError(
      "invalid_response",
      "Person response included authorization fields.",
    );
  }

  return person;
}

export async function ensureCurrentPersonViaHttp(
  input: EnsureCurrentPersonViaHttpInput,
): Promise<GlobalPerson> {
  const idToken = await input.getIdToken();

  if (!idToken) {
    throw new PersonIdentityError("authentication_required");
  }

  let response: Awaited<ReturnType<CurrentPersonFetch>>;
  try {
    response = await input.fetchClient(
      `${trimTrailingSlashes(input.apiBaseUrl)}/ensureCurrentPerson`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        method: "POST",
      },
    );
  } catch {
    throw new PersonIdentityError("network_failure");
  }

  if (!response.ok) {
    if (response.status === 401) {
      throw new PersonIdentityError("authentication_required");
    }
    if (response.status === 403) {
      throw new PersonIdentityError("forbidden");
    }
    throw new PersonIdentityError("unknown");
  }

  const body = await response.json();
  const bodyRecord = requireRecord(body, "ensureCurrentPerson response");
  return globalPersonFromApiResponse(bodyRecord.person);
}

export function personIdentityErrorMessage(code: PersonIdentityErrorCode): string {
  switch (code) {
    case "authentication_required":
      return "Authentication is required to resolve the current person.";
    case "network_failure":
      return "The current person could not be resolved because the network failed.";
    case "invalid_response":
      return "The current person response was invalid.";
    case "forbidden":
      return "The current account is not allowed to resolve a person.";
    case "unknown":
      return "The current person could not be resolved.";
  }
}

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

function requireRecord(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new PersonIdentityError("invalid_response", `${name} must be an object.`);
  }

  return value as Record<string, unknown>;
}

function readRequiredString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new PersonIdentityError("invalid_response", `${key} must be a non-empty string.`);
  }
  return value;
}

function readNullableString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    throw new PersonIdentityError("invalid_response", `${key} must be a string or null.`);
  }
  return value;
}

function readRequiredBoolean(record: Record<string, unknown>, key: string): boolean {
  const value = record[key];
  if (typeof value !== "boolean") {
    throw new PersonIdentityError("invalid_response", `${key} must be a boolean.`);
  }
  return value;
}

function readGlobalPersonStatus(value: unknown): GlobalPersonStatus {
  if (globalPersonStatuses.includes(value as GlobalPersonStatus)) {
    return value as GlobalPersonStatus;
  }
  throw new PersonIdentityError("invalid_response", "Person status is invalid.");
}

function readGlobalPersonSource(value: unknown): GlobalPersonSource["createdBy"] {
  if (
    value === "system:ensureCurrentPerson" ||
    value === "seed:development" ||
    value === "migration:hvicc"
  ) {
    return value;
  }
  throw new PersonIdentityError("invalid_response", "Person source is invalid.");
}

function readNullableLegacySource(
  record: Record<string, unknown>,
  key: string,
): GlobalPersonMigrationMetadata["legacySource"] {
  const value = record[key];
  if (value === null || value === undefined || value === "hvicc_flutterflow_users") {
    return value ?? null;
  }
  throw new PersonIdentityError("invalid_response", "Person migration source is invalid.");
}
